import {readAsTextAsync} from "@/services/fileParser";
import * as moment from "moment";
import {Ref, ref, UnwrapRef} from "vue";
import {
    ApiCallErrorDisplayModel,
    BulkImportAction,
    CaseMap,
    CaseObjectRaw,
    InvalidFiles,
    Issue,
    WarningItem,
} from "../types";
import {BulkImportCase} from "@/components/Case/CaseBulkImport/composables/BulkImportCase"
import {delay} from "@/utils";
import axios from "axios";
import {capitalize} from "@/utils/filters";


export default () => {
    const caseTitleToCaseObject: CaseMap = new Map()

    const isVisible = ref(true)
    const isWarning = ref(true)
    const propagateToCluster = ref(true)
    // include singleton (one-address) clusters in propagation — off by default (#1209)
    const propagateToSingletons = ref(false)
    const progress: Ref<UnwrapRef<{ completeCount: number, total: number }>> = ref({completeCount: 0, total: 1})
    const globalCancelToken = ref(axios.CancelToken.source())
    const isCanceled = ref(false)
    const casesCreatedCount = ref(0)
    const casesEditedCount = ref(0)
    const addressesImportedTotal = ref(0)
    const casesLoadedTotal = ref(0)
    const addressesToImportTotal = ref(0)
    const failedImports: Ref<ApiCallErrorDisplayModel[]> = ref([])
    const isImportFinished = ref(false)
    const loading = ref(false)
    const isInputValid = ref(false)
    const warningItems: Ref<WarningItem[]> = ref([])
    const isFileUploadFinished = ref(false)
    const invalidFiles: InvalidFiles = ref([])
    const cjToId_dok: Map<string, Set<string>> = new Map()

    const resetState = () => {
        caseTitleToCaseObject.clear()
        cjToId_dok.clear()
        invalidFiles.value = []
        isVisible.value = true
        isWarning.value = true
        propagateToCluster.value = true
        propagateToSingletons.value = false
        progress.value.completeCount = 0
        progress.value.total = 0
        globalCancelToken.value = axios.CancelToken.source()
        isCanceled.value = false
        casesCreatedCount.value = 0
        casesEditedCount.value = 0
        addressesImportedTotal.value = 0
        casesLoadedTotal.value = 0
        addressesToImportTotal.value = 0
        failedImports.value = []
        isImportFinished.value = false
        loading.value = false
        isInputValid.value = false
        warningItems.value = []
        isFileUploadFinished.value = false
    }

    const formatError = (error) => {
        let result = ""

        if (error.errors) {
            // for each key write "header" (key), then iterate over all items of that key and add row per item + \n
            Object.keys(error.errors).forEach(key => {
                result += `${capitalize(key)}\n`
                error.errors[key].forEach(item => result += `${item}\n`)
                result += "\n"
            })
        } else if (error.message) {
            result = error.message
        } else {
            result = "Unexpected error"
        }

        return result
    }

    const getDocumentIdsForCjs = (cjs: string[]): string[] =>
    {
        const result = []
        cjs.forEach(cj => {
            if (cjToId_dok.has(cj))
                result.push(...Array.from(cjToId_dok.get(cj)))
        })
        return result
    }

    const exportErrors = () => {
        let csv = 'Type,\"Case Title (cj)\",\"id_dok\",\"Source File\",Issue';

        failedImports.value.forEach(item => {
            item.files.forEach(file => {
                csv += "\n\"import error\","
                csv += `${item.title}`
                csv += `\"${getDocumentIdsForCjs([item.title]).join("\n")}\",`
                csv += `"${file}",`
                csv += `"${item.error}"`
            })
        })

        invalidFiles.value.forEach(item => {
            csv += "\nerror,"
            csv += `${item.cj},`
            csv += `\"${getDocumentIdsForCjs([item.cj]).join("\n")}\",`
            csv += `"${item.name}",`
            csv += `"${item.issues}"`
        })

        warningItems.value.forEach(item => {
            csv += "\nwarning,"
            csv += `${item.title},`
            csv += `\"${getDocumentIdsForCjs([item.title]).join("\n")}\",`
            csv += `\"${item.file.reverse().join("\n")}\",`
            csv += `"${item.address ? item.address + " - " : ""}${item.issue}"`
        })

        const anchor = document.createElement('a');
        anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
        anchor.target = '_blank';
        anchor.download = 'caseBulkImportIssues.csv';
        anchor.click();
    }

    const handleImport = async (categoryName: string) => {
        loading.value = true
        const cases = Array.from(caseTitleToCaseObject.values())

        progress.value.total = casesLoadedTotal.value

        for (const caseObject of cases) {
            if (isCanceled.value) return

            const totalAddressCount = caseObject.addresses.supported.size + caseObject.addresses.unsupported.size
            if (totalAddressCount === 0) {
                progress.value.completeCount++
                continue
            }

            // ensure case exists
            const {caseFound, caseCreated} = await caseObject.ensureCaseExists(
                !isVisible.value,
                isWarning.value,
                globalCancelToken
            )

            if (caseCreated) casesCreatedCount.value++

            if (!caseFound && !caseCreated) {
                progress.value.completeCount++
                continue
            }

            // update remote
            caseObject.modifyDescription()
            const remoteCaseUpdated = await caseObject.updateRemote(globalCancelToken)

            if (!remoteCaseUpdated) {
                continue
            }

            if (!caseObject.wasCaseJustCreated) {
                casesEditedCount.value++
            }

            // import addresses
            await caseObject.importAddresses(propagateToCluster.value, propagateToCluster.value && propagateToSingletons.value, categoryName, globalCancelToken)

            progress.value.completeCount++
        }
        await delay(200)

        failedImports.value = getImportErrors()
        isImportFinished.value = true
        loading.value = false
        isInputValid.value = false
    }

    const getImportErrors = (): ApiCallErrorDisplayModel[] => {
        const result: ApiCallErrorDisplayModel[] = []

        caseTitleToCaseObject.forEach(caseObject => {
            // Process Address Import Errors
            caseObject.addressImportErrors.forEach((error, curr) => {
                const fileSet = error.addresses.reduce<Set<string>>((acc, {sourceFiles}) => {
                    sourceFiles.forEach(file => acc.add(file));
                    return acc;
                }, new Set());

                result.push({
                    error: formatError(error),
                    action: BulkImportAction.AddressImport,
                    files: Array.from(fileSet), // fix - files should be only the files where the failed addresses were
                    title: caseObject.title,
                    addresses: error.addresses.map(address => address.address),
                    currency: curr
                })
            })

            // Process Case Api Call Errors
            caseObject.caseApiCallErrors.forEach((errorMessage, action) => {
                result.push({
                    error: formatError({message: errorMessage}),
                    action: action,
                    files: Array.from(caseObject.sourceFiles),
                    title: caseObject.title,
                    currency: "",
                    addresses: Array.from(caseObject.addresses.supported.keys())
                })
            })

            addressesImportedTotal.value += caseObject.successfullyImportedAddressCount
        })

        return result
    }

    const validateFile = (caseObject: CaseObjectRaw) => {
        let invalidDate = false
        const missingAttributes = []

        if (caseObject.id_dok === undefined || caseObject.id_dok === null) missingAttributes.push("id_dok")

        if (!caseObject.cj) missingAttributes.push("cj")

        if (!caseObject.datum_zapis) missingAttributes.push("datum_zapis")

        if (!moment(caseObject.datum_zapis).isValid()) invalidDate = true

        let issues = `${missingAttributes.length > 0 ? "Missing attributes: " : ""}${missingAttributes.join(", ")}`

        if (invalidDate) {
            if (issues.length > 0) issues += "; "
            issues += "Invalid date in datum_zapis"
        }

        return issues
    }

    const handleCaseObjectJoining = (caseObjectRaw: CaseObjectRaw) => {
        if (caseTitleToCaseObject.has(caseObjectRaw.cj)) {
            const caseObject = caseTitleToCaseObject.get(caseObjectRaw.cj)

            caseObject.updateReportedAt(caseObjectRaw.datum_zapis)

            caseObject.writeAddresses(caseObjectRaw)

        } else {
            const newCase: BulkImportCase = new BulkImportCase(caseObjectRaw.cj, caseObjectRaw.datum_zapis)
            newCase.writeAddresses(caseObjectRaw)

            caseTitleToCaseObject.set(caseObjectRaw.cj, newCase)
        }
    }

    const validateInput = () => {
        let validAddressCount = 0

        caseTitleToCaseObject.forEach(caseObject =>
            validAddressCount += caseObject.addresses.supported.size)

        if (validAddressCount > 0 && invalidFiles.value.length === 0) {
            isInputValid.value = true
        } else {
            isInputValid.value = false
        }
    }

    const formatWarningItems = () => {
        caseTitleToCaseObject.forEach(caseObject => {
            const hasCaseDataForImport = caseObject.addresses.supported.size === 0 && caseObject.invalidAddresses.size === 0

            // check if dok_id is zero
            caseObject.addresses.supported.forEach((addressData) => {
               if (addressData.documents.has(0))
               {
                   warningItems.value.push({
                       title: caseObject.title,
                       file: Array.from(caseObject.sourceFiles),
                       address: "",
                       issue: Issue.IdDokZero
                   })
               }
            });

            // handle empty Case Data
            if (hasCaseDataForImport) {
                warningItems.value.push({
                    title: caseObject.title,
                    file: Array.from(caseObject.sourceFiles),
                    address: "",
                    issue: caseObject.shouldHaveAddressesToImport ? Issue.UnsupportedCurrencies : Issue.EmptyData
                })
            } else { // handle invalid address items
                caseObject.invalidAddresses.forEach((sourceFileToUnits, address) => {
                    sourceFileToUnits.forEach((currencyUnitSet, file) => {
                        warningItems.value.push({
                            title: caseObject.title,
                            file: [file],
                            address: address,
                            issue: `${Issue.InvalidAddress} (${Array.from(currencyUnitSet).join(",")})` as Issue
                        })
                    })
                })
            }
        })
    }

    const onUpload = async (files: File[]) => {
        const handleFileParseCallbackArray = Array.from(files).map(async file => {
            if (file.type !== "application/json") {
                return
            }

            const fileContent = await readAsTextAsync(file)
            let parsedContent: CaseObjectRaw
            try {
                parsedContent = JSON.parse(fileContent)
            } catch (error) {
                invalidFiles.value.push({name: file.name, cj: "Could not be read" , issues: error.message})
                return
            }

            if (parsedContent.cj)
            {
                if(cjToId_dok.has(parsedContent.cj))
                {
                    cjToId_dok.get(parsedContent.cj).add(parsedContent.id_dok.toString())
                } else
                {
                    cjToId_dok.set(parsedContent.cj, new Set([parsedContent.id_dok.toString()]))
                }
            }

            const issues = validateFile(parsedContent)
            const isValid = issues === ""

            if (isValid) {
                parsedContent.sourceFile = file.name
                handleCaseObjectJoining(parsedContent)
            } else {
                invalidFiles.value.push({name: file.name, cj: parsedContent.cj, issues: issues})
            }
        })

        await Promise.all(handleFileParseCallbackArray)

        caseTitleToCaseObject.forEach(caseObject => {
            if (caseObject.addresses.supported.size + caseObject.addresses.unsupported.size > 0) {
                casesLoadedTotal.value += 1
                addressesToImportTotal.value += caseObject.addresses.supported.size
            }
        })

        validateInput()
        formatWarningItems()
        isFileUploadFinished.value = true
    }

    const getNumberOfIgnoredAddresses = ():number => {
        let totalNumberOfIgnoredAddresses = 0
        caseTitleToCaseObject.forEach(caseObj => {
            totalNumberOfIgnoredAddresses += caseObj.addresses.unsupported.size
        })
        return totalNumberOfIgnoredAddresses
    }

    return {
        onUpload,
        handleImport,
        isVisible,
        isWarning,
        propagateToCluster,
        propagateToSingletons,
        caseTitleToCaseObject,
        invalidFiles,
        resetState,
        progress,
        globalCancelToken,
        isCanceled,
        casesCreatedCount,
        casesEditedCount,
        casesLoadedTotal,
        addressesToImportTotal,
        addressesImportedTotal,
        failedImports,
        isImportFinished,
        loading,
        isInputValid,
        warningItems,
        isFileUploadFinished,
        getNumberOfIgnoredAddresses,
        exportErrors
    }
}

